import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Megaphone, ArrowRight, Trash2, X, TrendingUp, Send, Users, MessageSquare } from 'lucide-react';

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchCampaigns = async () => {
    try {
      const res = await api.get('/campaigns');
      setCampaigns(res.data.campaigns || []);
    } catch { toast.error('Failed to load campaigns.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const handleDelete = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await api.delete(`/campaigns/${id}`);
      setCampaigns(c => c.filter(x => x.id !== id));
      toast.success('Campaign deleted.');
    } catch { toast.error('Failed to delete.'); }
  };

  if (loading) return (
    <div style={{ padding:32, display:'flex', alignItems:'center', gap:12, color:'var(--muted)' }}>
      <div className="spinner" /> Loading...
    </div>
  );

  return (
    <div style={{ padding:32, maxWidth:1100 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, letterSpacing:'-.02em' }}>Campaigns</h1>
          <p style={{ color:'var(--muted)', fontSize:13, marginTop:4 }}>
            {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={14} /> New Campaign
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div style={{
          border:'2px dashed var(--border)', borderRadius:16,
          padding:'64px 32px', textAlign:'center'
        }}>
          <div style={{
            width:56, height:56, borderRadius:14,
            background:'var(--glow)', display:'flex',
            alignItems:'center', justifyContent:'center',
            margin:'0 auto 16px'
          }}>
            <Megaphone size={24} color="var(--accent)" />
          </div>
          <p style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>No campaigns yet</p>
          <p style={{ color:'var(--muted)', fontSize:13, marginBottom:20, maxWidth:320, margin:'0 auto 20px' }}>
            Create your first campaign to start sending AI-personalized cold emails
          </p>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={13} /> Create Campaign
          </button>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16 }}>
          {campaigns.map(c => {
            const openRate = c.emails_sent > 0 ? Math.round((c.emails_opened/c.emails_sent)*100) : 0;
            const replyRate = c.emails_sent > 0 ? Math.round((c.replies_received/c.emails_sent)*100) : 0;
            const badgeClass = { active:'badge-green', paused:'badge-amber', draft:'badge-gray', completed:'badge-purple' }[c.status] || 'badge-gray';

            return (
              <Link key={c.id} to={`/campaigns/${c.id}`} style={{ textDecoration:'none' }}>
                <div style={{
                  background:'var(--card)',
                  border:'1px solid var(--border)',
                  borderRadius:14,
                  padding:22,
                  cursor:'pointer',
                  transition:'all .2s',
                  position:'relative',
                  overflow:'hidden',
                  boxShadow:'var(--shadow)'
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor='rgba(108,71,255,.35)';
                    e.currentTarget.style.transform='translateY(-2px)';
                    e.currentTarget.style.boxShadow='0 4px 20px rgba(108,71,255,.1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor='var(--border)';
                    e.currentTarget.style.transform='';
                    e.currentTarget.style.boxShadow='var(--shadow)';
                  }}
                >
                  {/* Top accent line */}
                  <div style={{
                    position:'absolute', top:0, left:0, right:0, height:3,
                    background: c.status === 'active'
                      ? 'linear-gradient(90deg, #16a34a, #4ade80)'
                      : 'linear-gradient(90deg, var(--accent), var(--accent2))',
                    opacity: c.status === 'active' ? 1 : 0.4
                  }}/>

                  {/* Header */}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
                    <div style={{ flex:1, paddingRight:32 }}>
                      <span className={`badge ${badgeClass}`} style={{ marginBottom:8, display:'inline-flex' }}>
                        {c.status}
                      </span>
                      <h3 style={{ fontWeight:700, fontSize:15, lineHeight:1.3, color:'var(--text)' }}>
                        {c.name}
                      </h3>
                      {c.subject_template && (
                        <p style={{
                          fontSize:12, color:'var(--muted)', marginTop:4,
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'
                        }}>
                          {c.subject_template}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={(e) => handleDelete(c.id, e)}
                      className="btn btn-ghost"
                      style={{ position:'absolute', top:14, right:14, padding:6 }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>

                  {/* Stats */}
                  <div style={{
                    display:'grid', gridTemplateColumns:'repeat(4,1fr)',
                    gap:8, paddingTop:14,
                    borderTop:'1px solid var(--border)'
                  }}>
                    {[
                      { icon: Send, label:'Sent', value: c.emails_sent||0, color:'#7c3aed' },
                      { icon: TrendingUp, label:'Opens', value:`${openRate}%`, color:'#f59e0b' },
                      { icon: MessageSquare, label:'Replies', value:`${replyRate}%`, color:'#16a34a' },
                      { icon: Users, label:'Leads', value: c.lead_count||0, color:'#3b82f6' },
                    ].map(({ icon: Icon, label, value, color }) => (
                      <div key={label} style={{
                        background:'var(--bg3)',
                        borderRadius:8, padding:'10px 8px',
                        textAlign:'center'
                      }}>
                        <Icon size={12} color={color} style={{ marginBottom:4, display:'block', margin:'0 auto 4px' }}/>
                        <div style={{ fontSize:16, fontWeight:800, color:'var(--text)', lineHeight:1 }}>{value}</div>
                        <div style={{ fontSize:10.5, color:'var(--muted)', marginTop:3, fontWeight:500 }}>{label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Footer */}
                  <div style={{ display:'flex', justifyContent:'flex-end', marginTop:12 }}>
                    <span style={{
                      fontSize:12, color:'var(--accent)',
                      display:'flex', alignItems:'center', gap:4,
                      fontWeight:600
                    }}>
                      Open campaign <ArrowRight size={11} />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateCampaignModal
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); fetchCampaigns(); }}
        />
      )}
    </div>
  );
}

function CreateCampaignModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ name:'', subject_template:'', follow_up_enabled:false, follow_up_days:3 });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/campaigns', form);
      toast.success('Campaign created!');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h2 style={{ fontSize:17, fontWeight:700 }}>New Campaign</h2>
            <p style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>Set up your outreach campaign</p>
          </div>
          <button className="btn btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div>
            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:6, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.04em' }}>
              Campaign Name *
            </label>
            <input
              className="input"
              placeholder="e.g. SaaS Founders Outreach"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              required
            />
          </div>
          <div>
            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:6, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.04em' }}>
              Campaign Goal / Context
            </label>
            <textarea
              className="input"
              placeholder="Describe what you're selling, who you're targeting, and what outcome you want. The AI uses this to write better emails."
              value={form.subject_template}
              onChange={e => setForm(f => ({ ...f, subject_template: e.target.value }))}
              style={{ minHeight:100 }}
            />
            <p style={{ fontSize:11, color:'var(--muted)', marginTop:5 }}>
              💡 The more context you give, the better the AI emails will be.
            </p>
          </div>
          <div style={{
            background:'var(--bg3)',
            border:'1px solid var(--border)',
            borderRadius:10, padding:16
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input
                type="checkbox" id="fu"
                checked={form.follow_up_enabled}
                onChange={e => setForm(f => ({ ...f, follow_up_enabled: e.target.checked }))}
                style={{ accentColor:'var(--accent)', width:16, height:16, flexShrink:0 }}
              />
              <div style={{ flex:1 }}>
                <label htmlFor="fu" style={{ fontSize:13, fontWeight:600, cursor:'pointer', display:'block' }}>
                  Enable automatic follow-ups
                </label>
                <p style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>
                  Follow-ups get 2-3× more replies than first emails
                </p>
              </div>
              {form.follow_up_enabled && (
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontSize:12, color:'var(--muted)' }}>after</span>
                  <input
                    className="input"
                    type="number"
                    value={form.follow_up_days}
                    onChange={e => setForm(f => ({ ...f, follow_up_days: parseInt(e.target.value)||3 }))}
                    style={{ width:56, textAlign:'center', padding:'6px 8px' }}
                    min={1} max={30}
                  />
                  <span style={{ fontSize:12, color:'var(--muted)' }}>days</span>
                </div>
              )}
            </div>
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:4 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}